'use client';

import React, { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

export const DATA_TABLE_DEFAULT_MAX_INITIAL_ROWS = 100;
export const DATA_TABLE_DEFAULT_VIRTUALIZE_AT = 100;

export interface DataTableColumn<T> {
    key: string;
    label: string;
    sortable?: boolean;
    /** Custom cell renderer. Falls back to data[key] as string. */
    render?: (_row: T, _index: number) => React.ReactNode;
    /** Right-align the column (for numbers/currency). */
    align?: 'left' | 'right' | 'center';
    /** Additional header className */
    headerClassName?: string;
    /** Additional cell className */
    cellClassName?: string;
}

interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    data: T[];
    /** Key extractor — defaults to index. Provide for stable React keys. */
    keyExtractor?: (_row: T, _index: number) => string | number;
    emptyMessage?: string;
    emptyIcon?: React.ReactNode;
    onRowClick?: (_row: T, _index: number) => void;
    loading?: boolean;
    /** Number of skeleton rows to show while loading (default: 5). */
    loadingRows?: number;
    className?: string;
    /** Default sort column key */
    defaultSortKey?: string;
    /** Default sort direction */
    defaultSortDir?: 'asc' | 'desc';
    /** Desktop row count that switches the table body to virtualized rendering. */
    virtualizeAt?: number;
    /** Mobile card cap for large datasets; desktop still virtualizes the full sorted list. */
    maxInitialRows?: number;
    /** Color theme. Defaults to the dark dashboard theme. Use 'light' on
     *  customer-facing light surfaces (portal/public/sub) — the dark defaults
     *  (text-zinc-300 cells, bg-bg-surface mobile cards) are unreadable on a
     *  white/gray-50 page. */
    theme?: 'dark' | 'light';
}

/** Theme-dependent class fragments for the table chrome. */
function dataTableTheme(theme: 'dark' | 'light') {
    const light = theme === 'light';
    return {
        border: light ? 'border-gray-200' : 'border-border-subtle',
        rowBorder: light ? 'border-gray-100' : 'border-white/5',
        headerText: light ? 'text-gray-500' : 'text-zinc-400',
        headerHover: light ? 'hover:text-gray-700' : 'hover:text-zinc-300',
        cellText: light ? 'text-gray-700' : 'text-zinc-300',
        stickyBg: light ? 'bg-white' : 'bg-bg-surface',
        rowHover: light ? 'hover:bg-gray-50' : 'hover:bg-white/[0.02]',
        cardBg: light ? 'bg-white' : 'bg-bg-surface',
        cardActive: light ? 'active:bg-gray-50' : 'active:bg-white/[0.02]',
        labelText: light ? 'text-gray-500' : 'text-zinc-400',
        valueText: light ? 'text-gray-700' : 'text-zinc-300',
        noteBg: light ? 'bg-gray-50' : 'bg-bg-surface',
        noteText: light ? 'text-gray-500' : 'text-zinc-500',
        // `!` important: Skeleton raw-concats its dark `bg-zinc-800/60` base
        // without tailwind-merge, so a plain `bg-gray-200` could lose on source
        // order. The important modifier guarantees the light fill wins.
        skeletonBg: light ? '!bg-gray-200' : '',
    };
}

const alignClasses = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
} as const;

function SortArrow({ dir, className = 'text-zinc-400' }: { dir: 'asc' | 'desc' | null; className?: string }) {
    return (
        <span className={`inline-flex ml-1 ${className}`} aria-hidden>
            {dir === 'asc' ? '\u25B2' : dir === 'desc' ? '\u25BC' : '\u25B4'}
        </span>
    );
}

function compareValues(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function DataTable<T extends object>({
    columns,
    data,
    keyExtractor,
    emptyMessage = 'No data',
    emptyIcon,
    onRowClick,
    loading = false,
    loadingRows = 5,
    className = '',
    defaultSortKey,
    defaultSortDir = 'asc',
    virtualizeAt = DATA_TABLE_DEFAULT_VIRTUALIZE_AT,
    maxInitialRows = DATA_TABLE_DEFAULT_MAX_INITIAL_ROWS,
    theme = 'dark',
}: DataTableProps<T>) {
    const t = dataTableTheme(theme);
    const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);
    const scrollRef = useRef<HTMLDivElement>(null);

    const sortedData = useMemo(() => {
        if (!sortKey) return data;
        const col = columns.find((c) => c.key === sortKey);
        if (!col?.sortable) return data;
        return [...data].sort((a, b) => {
            const aVal = (a as Record<string, unknown>)[sortKey];
            const bVal = (b as Record<string, unknown>)[sortKey];
            const cmp = compareValues(aVal, bVal);
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [data, sortKey, sortDir, columns]);

    const shouldVirtualizeDesktop = sortedData.length >= virtualizeAt;

     
    const rowVirtualizer = useVirtualizer({
        count: sortedData.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 48,
        overscan: 10,
        enabled: shouldVirtualizeDesktop,
    });

    const virtualItems = shouldVirtualizeDesktop ? rowVirtualizer.getVirtualItems() : [];
    const topPadding = shouldVirtualizeDesktop ? (virtualItems[0]?.start ?? 0) : 0;
    const bottomPadding = shouldVirtualizeDesktop && virtualItems.length > 0
        ? rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1].start + virtualItems[virtualItems.length - 1].size)
        : 0;
    const desktopRows = shouldVirtualizeDesktop
        ? virtualItems.map((item) => ({ row: sortedData[item.index], rowIdx: item.index }))
        : sortedData.map((row, rowIdx) => ({ row, rowIdx }));
    const mobileRows = sortedData.length > maxInitialRows ? sortedData.slice(0, maxInitialRows) : sortedData;

    const handleSort = (col: DataTableColumn<T>) => {
        if (!col.sortable) return;
        if (sortKey === col.key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(col.key);
            setSortDir('asc');
        }
    };

    const renderCell = (row: T, rowIdx: number, col: DataTableColumn<T>) => {
        const rec = row as Record<string, unknown>;
        const cellValue = col.render
            ? col.render(row, rowIdx)
            : String(rec[col.key] ?? '');
        const isNumeric = !col.render && typeof rec[col.key] === 'number';
        return (
            <td
                key={col.key}
                className={`
                    px-4 py-3 text-sm ${t.cellText}
                    ${isNumeric ? 'tabular-nums' : ''}
                    ${alignClasses[col.align ?? 'left']}
                    ${col.cellClassName ?? ''}
                `}
            >
                {cellValue}
            </td>
        );
    };

    if (loading) {
        return (
            <div className={className} data-large-boundary="DataTable">
                {/* Desktop: skeleton table */}
                <div className="hidden md:block">
                    <div className={`border ${t.border} rounded-xl overflow-hidden`}>
                        {/* Header skeleton */}
                        <div className={`flex items-center gap-4 px-4 py-3 border-b ${t.rowBorder}`}>
                            {columns.map((col) => (
                                <Skeleton key={col.key} className={`h-3 flex-1 ${t.skeletonBg}`} />
                            ))}
                        </div>
                        {Array.from({ length: loadingRows }).map((_, i) => (
                            <div key={i} className={`flex items-center gap-4 px-4 py-3 border-b ${t.rowBorder} last:border-b-0`}>
                                {columns.map((col, j) => (
                                    <Skeleton key={col.key} className={`h-3 ${j === 0 ? 'w-1/4' : 'flex-1'} ${t.skeletonBg}`} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                {/* Mobile: skeleton cards */}
                <div className="md:hidden space-y-3">
                    {Array.from({ length: loadingRows }).map((_, i) => (
                        <div key={i} className={`${t.cardBg} border ${t.border} rounded-xl p-4 space-y-3`}>
                            <Skeleton className={`h-4 w-1/3 ${t.skeletonBg}`} />
                            <Skeleton className={`h-3 w-full ${t.skeletonBg}`} />
                            <Skeleton className={`h-3 w-2/3 ${t.skeletonBg}`} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className={className} data-large-boundary="DataTable">
                <EmptyState
                    icon={emptyIcon}
                    title={emptyMessage}
                    theme={theme}
                />
            </div>
        );
    }

    return (
        <div className={className} data-large-boundary="DataTable">
            {/* Desktop: standard table */}
            <div
                ref={scrollRef}
                className={`hidden md:block border ${t.border} rounded-xl overflow-auto ${shouldVirtualizeDesktop ? 'max-h-[640px]' : 'overflow-hidden'}`}
            >
                <table className="w-full">
                    <thead className={shouldVirtualizeDesktop ? `sticky top-0 z-10 ${t.stickyBg}` : undefined}>
                        <tr className={`border-b ${t.rowBorder}`}>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={col.sortable ? () => handleSort(col) : undefined}
                                    className={`
                                        px-4 py-3
                                        text-xs ${t.headerText} uppercase tracking-wider font-medium
                                        ${alignClasses[col.align ?? 'left']}
                                        ${col.headerClassName ?? ''}
                                        ${col.sortable ? `cursor-pointer select-none ${t.headerHover} transition-colors` : ''}
                                    `}
                                >
                                    {col.label}
                                    {col.sortable && (
                                        <SortArrow dir={sortKey === col.key ? sortDir : null} className={t.headerText} />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {topPadding > 0 && (
                            <tr aria-hidden="true">
                                <td colSpan={columns.length} style={{ height: topPadding, padding: 0 }} />
                            </tr>
                        )}
                        {desktopRows.map(({ row, rowIdx }) => {
                            const key = keyExtractor ? keyExtractor(row, rowIdx) : rowIdx;
                            return (
                                <tr
                                    key={key}
                                    onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                    onKeyDown={
                                        onRowClick
                                            ? (e) => {
                                                  if (e.key === 'Enter') onRowClick(row, rowIdx);
                                              }
                                            : undefined
                                    }
                                    className={`
                                        border-b ${t.rowBorder} last:border-b-0
                                        transition-colors
                                        ${onRowClick ? `cursor-pointer ${t.rowHover}` : ''}
                                    `}
                                >
                                    {columns.map((col) => renderCell(row, rowIdx, col))}
                                </tr>
                            );
                        })}
                        {bottomPadding > 0 && (
                            <tr aria-hidden="true">
                                <td colSpan={columns.length} style={{ height: bottomPadding, padding: 0 }} />
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile: card layout */}
            <div className="md:hidden space-y-3">
                {mobileRows.map((row, rowIdx) => {
                    const key = keyExtractor ? keyExtractor(row, rowIdx) : rowIdx;
                    return (
                        <div
                            key={key}
                            onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
                            className={`
                                ${t.cardBg} border ${t.border} rounded-xl p-4 space-y-2
                                ${onRowClick ? `cursor-pointer ${t.cardActive}` : ''}
                            `}
                        >
                            {columns.map((col) => {
                                const rec = row as Record<string, unknown>;
                                const cellValue = col.render
                                    ? col.render(row, rowIdx)
                                    : String(rec[col.key] ?? '');
                                const isNumeric = !col.render && typeof rec[col.key] === 'number';
                                return (
                                    <div key={col.key} className="flex items-baseline justify-between gap-2">
                                        <span className={`text-xs ${t.labelText} font-medium uppercase tracking-wider shrink-0`}>
                                            {col.label}
                                        </span>
                                        <span className={`text-sm ${t.valueText} text-right ${isNumeric ? 'tabular-nums' : ''}`}>
                                            {cellValue}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
                {sortedData.length > mobileRows.length && (
                    <div className={`rounded-xl border ${t.rowBorder} ${t.noteBg} px-4 py-3 text-center text-xs ${t.noteText}`}>
                        Showing first {mobileRows.length} of {sortedData.length} rows. Refine filters for more specific results.
                    </div>
                )}
            </div>
        </div>
    );
}
